import { useRouter } from 'next/router';
import * as React from 'react';
import { encryptData } from '@doltech/core/lib/encrypt-page-data/encryptData';
import { LandingSharedLayoutProps } from '@doltech/ui/lib/figma/Layout/Landing/hocs/withLandingSharedLayout';
import { CommonSlugPage } from '@doltech/ui/lib/shared/components/SlugPage/CommonSlugPage';
import { PageTemplate } from '../routers/route.template';
import { bootstrapAuthManager } from '../bootstrapSSRAuthManager';
import {
  getLandingGrammarPageByTemplateTypes,
  getSeoPageLandingGrammarByPageId,
  getSeoPageLandingGrammarByUrl,
} from '@doltech/domains/landing-grammar/lib/shared/apis/landing-grammar';
import {
  GrammarTemplateTypeEnum,
  GrammarTemplateTypeList,
} from '@doltech/domains/landing-grammar/lib/shared/constants';
import { getDataPreRendering } from '../routers/route';
import { environment } from '@doltech/core/lib/environment/environment';
import { getFinalUrlGrammar } from '@doltech/domains/landing-grammar/lib/shared/utils';
import PageNotFound from './404';
import { fetchRedirectPage } from '@doltech/ui/lib/figma/Layout/Landing/api/pages-landing.m2m';
import { fixRedirectUrl } from '@doltech/utils/lib/url';
import { cacheWrap } from '@doltech/core/lib/cache/cache-manager';
import { NextSeo } from 'next-seo';

//
export interface SlugPageProps extends LandingSharedLayoutProps {
  data?: any;
  error?: any;
  slug?: any;
  pageType?: string;
  structuralSeo?: any;
  schemaScriptString?: string;
  isStructuralSEO?: boolean;
  otherUrlInfo?: any;
  query?: any;
}

const SlugPageError = (error) => (
  <div>
    <pre>Response error: {JSON.stringify(error, null, 2)}</pre>
  </div>
);
const FallbackPage = (props: SlugPageProps) => {
  const { error } = props;
  const { isFallback } = useRouter();

  // React.useEffect(() => {
  //   if (!urlInfo && process.env.NODE_ENV === 'development') {
  //     reload();
  //   }
  // }, [urlInfo, reload]);

  if (error) {
    return <SlugPageError error={error}/>;
  }

  if (isFallback) {
    return (
      <div>
        <h1>Page not found</h1>
        <p>You may need to refresh this page, we need sometime to generate this page...</p>
      </div>
    );
  }

  return <SlugPage {...props} />;
};
export default FallbackPage;

/*
{!environment.production && (
          <meta name="robots" content="noindex,nofollow" />
        )}
 */

export const SlugPage = (props: SlugPageProps) => {
  const { slug, pageType, otherUrlInfo, data, query } = props;
  const canonicalUrl = data?.blogPageData?.google?.canonicalUrl
    ? data?.blogPageData?.google?.canonicalUrl
    : getFinalUrlGrammar(slug);

  const hardCodeHeadTags = React.useMemo(() => {
    if ([GrammarTemplateTypeEnum.LANDING_GRAMMAR_THEORY, GrammarTemplateTypeEnum.LANDING_GRAMMAR_EXERCISE].includes(pageType)) {
      const basePathUrl = getFinalUrlGrammar(slug);
      const hasPrev = query?.page > 1;
      const hasNext = query?.page < data?.theoryList?.data?.totalPages || query?.page < data?.exerciseList?.data?.totalPages;

      return `
        ${
        hasPrev &&
        `<link rel="prev" href="${basePathUrl}?topic=${query?.topic}&page=${
          Number(query?.page) - 1
        }" />`
      }
        ${
        hasNext &&
        `<link rel="next" href="${basePathUrl}?topic=${query?.topic}&page=${
          Number(query?.page) + 1
        }" />`
      }
        <link rel="canonical" href="${canonicalUrl}" />
        <meta name='dmca-site-verification' content='RmRSSnYwUEEwR2FoUzRZT2RBVnNOSzFvL3M3MFM3dmZCK0gwdUhvS3BuQT01' />
        ${!environment.production && '<meta name="robots" content="noindex,nofollow" />'}
      `;
    }
    return `
      <link rel="canonical" href="${canonicalUrl}" />
      <meta name='dmca-site-verification' content='RmRSSnYwUEEwR2FoUzRZT2RBVnNOSzFvL3M3MFM3dmZCK0gwdUhvS3BuQT01' />
      ${!environment.production && '<meta name="robots" content="noindex,nofollow" />'}
    `;
  }, [pageType, canonicalUrl]);
  if (pageType === 'NOT_FOUND_GRAMMAR') {
    return (
      <>
        <NextSeo title="Trang bạn tìm kiếm không tồn tại - DOL Grammar" noindex nofollow/>
        <PageNotFound/>;
      </>
    );
  }
  return (
    <CommonSlugPage {...props} hardCodeHeadTags={hardCodeHeadTags} urlInfo={data?.blogPageData}>
      {({ allProps }) => (
        <PageTemplate
          pageType={pageType}
          pageProps={allProps}
          data={data}
          otherUrlInfo={otherUrlInfo}
          query={query}
        />
      )}
    </CommonSlugPage>
  );
};

const buildSEOInfo = ({ urlInfo }) => {
  const structuralSeo = (urlInfo?.metadata?.toString() || '').replace(
    /\$REPLACE_DOMAIN_HERE\$/g,
    `${environment.REACT_APP_DOL_GRAMMAR_WEBSITE_HOST_NAME}/`,
  );
  return {
    structuralSeo: fixRedirectUrl(structuralSeo),
    schemaScriptString: urlInfo?.blogPageDTO?.google?.schemaHtml || '',
  };
};

export const getServerSideProps = async (context) => {
  bootstrapAuthManager();
  const { req, params, query } = context;
  const slug = params.slug.join('/').replace(/([^:])(\/\/+)/g, '$1/');
  let dataByUrl;
  // we'll check if any url in system match 100% with requested url
  // if no url matched => check if it's a structural url

  const checkRedirectUrl = await fetchRedirectPage({ url: req.url, app: 'GRAMMAR' });
  if (checkRedirectUrl?.targetUrl && checkRedirectUrl?.httpCode) {
    const redirectTo = fixRedirectUrl(checkRedirectUrl?.targetUrl);
    const statusCode = Number(checkRedirectUrl?.httpCode);
    console.log('seo-redirect-detected:', redirectTo, statusCode);
    return {
      redirect: {
        destination: redirectTo,
        statusCode,
      },
    };
  }

  if (slug === '404') {
    return {
      props: {},
    };
  }

  const { data: otherUrlInfo } = await cacheWrap(
    `getLandingGrammarPageByTemplateTypes_${JSON.stringify(GrammarTemplateTypeList)}`,
    async () => {
      return getLandingGrammarPageByTemplateTypes(GrammarTemplateTypeList);
    },
  );

  let notFound = false;
  try {
    const { data } = await cacheWrap(
      `getSeoPageLandingGrammarByUrl_${JSON.stringify(slug)}`,
      async () => {
        return getSeoPageLandingGrammarByUrl(slug);
      },
    );
    dataByUrl = data;
  } catch (err) {
    if (err === 'RESOURCE_NOT_FOUND') {
      notFound = true;
    }
  }

  const pageId = dataByUrl?.id;

  notFound = !dataByUrl || !pageId;

  if (notFound) {
    const { res } = context;
    res.statusCode = 404;
    return {
      props: encryptData({
        key: slug,
        slug,
        otherUrlInfo,
        pageType: 'NOT_FOUND_GRAMMAR',
        query,
      }),
    };
  }

  const { data: rawPageData } = await cacheWrap(
    `getSeoPageLandingGrammarByPageId_${pageId}`,
    () => {
      return getSeoPageLandingGrammarByPageId(pageId);
    },
  );
  const data = await cacheWrap(`getDataPreRendering_${pageId}_${JSON.stringify(query)}`, () =>
    getDataPreRendering(rawPageData, query),
  );
  const { structuralSeo, schemaScriptString } = buildSEOInfo({ urlInfo: rawPageData });

  return {
    props: encryptData({
      key: slug,
      slug,
      otherUrlInfo,
      pageType: notFound ? 'NOT_FOUND_GRAMMAR' : rawPageData?.templateTypeId,
      data: data?.pageData,
      isStructuralSEO: false,
      query,
      structuralSeo,
      schemaScriptString,
    }),
  };
};
